import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import Modal from 'components/ui/Modal';
import * as Notebooks from 'modules/notebooks';

/** Mirror the API's validation (1..100 title, <=500 description). */
const TITLE_MAX = 100;
const DESCRIPTION_MAX = 500;

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, the modal renames this notebook; otherwise it creates one. */
  notebook?: Pick<Notebooks.Types.Notebook, '_id' | 'title' | 'description' | 'isDefault'> | null;
  /** What the notebook is called on screen. Differs from `notebook.title` for the untouched default notebook. */
  displayTitle?: string;
  /** After a create or a rename that changed something; receives the saved notebook. */
  onSuccess: (notebook: Notebooks.Types.Notebook) => void;
}

export const NotebookFormModal: React.FC<Props> = ({ open, onClose, notebook, displayTitle, onSuccess }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createNotebook = Notebooks.Hooks.useCreate();
  const updateNotebook = Notebooks.Hooks.useUpdate();
  const isEditing = Boolean(notebook);
  const isPending = createNotebook.isPending || updateNotebook.isPending;
  const initialName = notebook ? (displayTitle ?? notebook.title) : '';

  // Seed the fields from the notebook when renaming (and reset on open/close).
  // Keyed on values, not the notebook object, so a list refetch never wipes what is being typed.
  const initialDescription = notebook?.description ?? '';
  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription);
  }, [initialName, initialDescription, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = name.trim();
    const about = description.trim();
    if (!title || isPending) return;
    try {
      if (!notebook) {
        onSuccess(await createNotebook.mutateAsync({ title, description: about || undefined }));
        return;
      }
      // Only send what changed, so renaming the default notebook's description
      // never overwrites its stored title with the localised label it is shown under.
      const patch: Notebooks.Types.IApi.Update.Request = {};
      if (title !== initialName) patch.title = title;
      if (about !== initialDescription) patch.description = about;
      if (patch.title === undefined && patch.description === undefined) {
        onClose();
        return;
      }
      onSuccess(await updateNotebook.mutateAsync({ id: notebook._id, ...patch }));
    } catch {
      // The hook already toasted the error; the dialog stays open for another try.
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={isEditing ? t('notebooks.rename_title') : t('notebooks.new_notebook')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notebook-title">{t('notebooks.name')}</Label>
          <Input
            id="notebook-title"
            name="title"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- the dialog exists to type this name
            autoFocus
            placeholder={t('notebooks.name_placeholder')}
            value={name}
            maxLength={TITLE_MAX}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notebook-description">{t('notebooks.description_optional')}</Label>
          <Textarea
            id="notebook-description"
            name="description"
            placeholder={t('notebooks.description_placeholder')}
            value={description}
            maxLength={DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={!name.trim() || isPending}>
            {isPending ? t('saving') : isEditing ? t('save') : t('notebooks.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default NotebookFormModal;
